// Vibrance — Photoshop-style Vibrance adjustment filter.
//
// Two sliders:
//   Vibrance (-100..+100):   intelligently boosts the saturation of
//                            less-saturated pixels more than already-
//                            saturated ones (positive), or reduces
//                            saturation of more-saturated pixels more
//                            (negative).
//   Saturation (-100..+100): simple linear saturation boost/reduction
//                            applied after vibrance.
//
// Algorithm operates in HSL space, touching only the S channel.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface VibranceParams {
  vibrance: number;
  saturation: number;
}

const DEFAULT_PARAMS: VibranceParams = {
  vibrance: 0,
  saturation: 0,
};

// ---------------------------------------------------------------------------
// HSL ↔ RGB conversion (standard cylindrical model)
// ---------------------------------------------------------------------------

interface HSL {
  h: number;
  s: number;
  l: number;
}

function rgbToHsl(r: number, g: number, b: number): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  if (max === rn) {
    h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
  } else if (max === gn) {
    h = ((bn - rn) / d + 2) * 60;
  } else {
    h = ((rn - gn) / d + 4) * 60;
  }

  return { h, s, l };
}

function hslToRgb(hsl: HSL): [number, number, number] {
  const { h, s, l } = hsl;

  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }

  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;

  let r1 = 0, g1 = 0, b1 = 0;
  if (hue < 60) { r1 = c; g1 = x; b1 = 0; }
  else if (hue < 120) { r1 = x; g1 = c; b1 = 0; }
  else if (hue < 180) { r1 = 0; g1 = c; b1 = x; }
  else if (hue < 240) { r1 = 0; g1 = x; b1 = c; }
  else if (hue < 300) { r1 = x; g1 = 0; b1 = c; }
  else { r1 = c; g1 = 0; b1 = x; }

  return [
    Math.round((r1 + m) * 255),
    Math.round((g1 + m) * 255),
    Math.round((b1 + m) * 255),
  ];
}

// ---------------------------------------------------------------------------
// Filter export
// ---------------------------------------------------------------------------

export const Vibrance: Filter<VibranceParams> = {
  id: "vibrance",
  displayName: "Vibrance",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: VibranceParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const vibrance = clampRange(params.vibrance, -100, 100);
    const satDelta = clampRange(params.saturation, -100, 100);

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      if (a === 0) {
        dst[o] = 0;
        dst[o + 1] = 0;
        dst[o + 2] = 0;
        dst[o + 3] = 0;
        continue;
      }

      let hsl = rgbToHsl(r, g, b);

      // Vibrance stage.
      if (vibrance !== 0) {
        let satAdjust: number;
        if (vibrance > 0) {
          satAdjust = vibrance * (1 - hsl.s) * (1 - hsl.s);
        } else {
          satAdjust = vibrance * hsl.s;
        }
        hsl.s = clamp01(hsl.s + satAdjust / 100);
      }

      // Saturation stage (linear adjustment applied after vibrance).
      if (satDelta !== 0) {
        if (satDelta > 0) {
          hsl.s = hsl.s + (satDelta / 100) * (1 - hsl.s);
        } else {
          hsl.s = hsl.s + (satDelta / 100) * hsl.s;
        }
        hsl.s = clamp01(hsl.s);
      }

      const [ro, go, bo] = hslToRgb(hsl);
      dst[o] = ro;
      dst[o + 1] = go;
      dst[o + 2] = bo;
      dst[o + 3] = a;
    }

    return out;
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return 0;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
